import { ButtonHTMLAttributes, forwardRef } from 'react'
import { Slot } from 'radix-ui'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Icon } from '../icon'
import { mtc } from '../../../utils/style-helpers'
import { iconLeftStyles, styles } from './styles'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'link'
  | 'pagination'
  | 'discreet'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Allows button to be an element other than button */
  asChild?: boolean
  disabled?: boolean
  /** NOTE: Please use regular icons for buttons */
  /** regular('coffee') */
  iconLeft?: IconDefinition
  iconLeftClassName?: string
  /** regular('coffee') */
  iconRight?: IconDefinition
  iconRightClassName?: string
  loading?: boolean
  /** Set the size of the button to one of: */
  size?: 'sm' | 'md' | 'lg' | 'square'
  /** Set the variant of the button to one of: */
  variant?:
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'danger'
    | 'link'
    | 'pagination'
    | 'discreet'
}

/** Returns the classes for a button variant without creating a button element */
export const buttonVariants = ({
  variant,
  size,
  className,
}: Pick<ButtonProps, 'variant' | 'size'> & { className?: string }) => {
  return mtc(styles({ variant, size, className }))
}

/** Displays a button or a button-like element */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      children,
      className,
      disabled,
      iconLeft,
      iconLeftClassName,
      iconRight,
      iconRightClassName,
      loading,
      size,
      variant,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot.Root : 'button'
    const leftIcon = loading ? solid('spinner-third') : iconLeft

    return (
      <Comp
        className={mtc(styles({ variant, size }), className)}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading}
        aria-label={loading ? 'Loading, please wait' : props['aria-label']}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {leftIcon && (
              <span
                role={loading ? 'status' : undefined}
                aria-live={loading ? 'polite' : undefined}
              >
                <Icon
                  icon={leftIcon}
                  className={mtc(
                    iconLeftStyles({ loading }),
                    iconLeftClassName
                  )}
                  color="text-current"
                />
              </span>
            )}

            {!loading && (
              <>
                {children}

                {iconRight && (
                  <Icon
                    icon={iconRight}
                    className={mtc('right-3 text-gray-500', iconRightClassName)}
                    color="text-current"
                  />
                )}
              </>
            )}
          </>
        )}
      </Comp>
    )
  }
)

Button.displayName = 'Button'
